import { useEffect } from 'react';
import { syncState } from '@legendapp/state';
import { supabase } from '@/lib/supabaseClient';
import { auth$ } from '@/stores/auth$';
import { ui$, setActiveNoteId } from '@/stores/ui$';
import { notes$, deleteNote } from '@/stores/notes$';
import { snippets$ } from '@/stores/snippets$';
import { isEmptyNote } from '@/lib/emptyNote';

// Only notes created in this browser session are candidates. Older empty notes
// are handled by the one-off cleanup SQL, never by this hook: on a fresh device
// notes can load before snippets, and a note with content but a default title
// would otherwise look empty.
const SESSION_STARTED_AT = Date.now();
const snippetsSync$ = syncState(snippets$);

const isCreatedThisSession = (note) => {
  const createdAt = Date.parse(note?.created_at ?? '');
  return Number.isFinite(createdAt) && createdAt >= SESSION_STARTED_AT;
};

const hasLiveShare = async (noteId) => {
  const user = auth$.user.get();
  if (!user?.id) return true;

  const { data, error } = await supabase
    .from('promptnote_shared_notes')
    .select('id')
    .eq('note_id', noteId)
    .eq('user_id', user.id)
    .eq('is_revoked', false)
    .limit(1)
    .maybeSingle();

  // On any error, assume it is shared so we never delete a possibly-shared note.
  if (error) return true;
  return !!data;
};

const getSnippetsArray = () => {
  const snippetsObj = snippets$.get();
  return snippetsObj && typeof snippetsObj === 'object' ? Object.values(snippetsObj) : [];
};

/**
 * Deletes a note the user just left if they never did anything to it.
 * Triggered only when activeNoteId moves away from a note, so a note being
 * created/edited is never touched. Soft-deletes via the existing deleteNote path.
 */
const cleanupLeftNote = async (noteId) => {
  if (!noteId) return;
  if (!auth$.user.get()?.id) return;

  // Snippets must be fully loaded, or a note with content looks empty.
  if (!snippetsSync$.isLoaded.get()) return;

  const note = notes$[noteId]?.get();
  if (!note) return;
  if (!isCreatedThisSession(note)) return;
  if (!isEmptyNote(note, getSnippetsArray())) return;

  if (await hasLiveShare(noteId)) return;

  const freshNote = notes$[noteId]?.get();
  if (!freshNote) return;
  if (!isEmptyNote(freshNote, getSnippetsArray(), { isShared: false })) return;

  deleteNote(noteId, (deletedId) => {
    if (ui$.activeNoteId.get() === deletedId) setActiveNoteId(null);
  });
};

export function useEmptyNoteCleanup() {
  useEffect(() => {
    const dispose = ui$.activeNoteId.onChange(({ getPrevious }) => {
      cleanupLeftNote(getPrevious());
    });
    return dispose;
  }, []);
}
